import * as React from 'react';
import Loading from '../../misc/components/loading';
import { DeviceTypeInteraction, DeviceInteractions, DeviceTypes, CoughSession, CoughSessionStatusEnum } from '../../misc/api/apiTypes';
import { DeviceAlerts } from './DeviceAlerts';
import Alert, { AlertCategoryEnum } from '../../misc/components/Alert';
import { ApiGetResults } from '../../misc/api/DeviceLinkApi/StatStripXpress2';
import { NoRecordingsPresent } from '../../misc/api/DeviceLinkApi/VitaloJak2Messages';
import { CloseInteractionModalAction, SetInteractionModalStepAction, SelectActionAction, DevicesAction, SetUploadStatusAction, ReceiveDeviceSessionsAction, SetDeviceOverallUploadStatusAction, UploadStatus } from '../DevicesNewReducer';
import { Device, ApiDevices } from '../../misc/api/DeviceLinkApi/Devices';
import { Visit, BloodGlucoseSession } from "webportalclient";
import * as VitaloJak2Api from "../../misc/api/DeviceLinkApi/VitaloJak2";
import * as GenericMessages from "../../misc/api/DeviceLinkApi/GenericMessages";
import { useDevicesStateContext } from '../DevicesStateContext';
import { NoSessionAvailable } from '../../misc/api/DeviceLinkApi/P230In2itiveMessages';
import AsyncActionButton from '../../misc/components/AsyncActionButton';
import { useWebPortalApiClient, useWebPortalLegacyClient } from '../../misc/api/WebPortalTokenClient';

export interface ActionSelectionProps {
    selectedOption?: string;
    options?: Array<DeviceTypeInteraction>;
    dispatch?: React.Dispatch<DevicesAction>;
}

export const ActionSelection = (props: ActionSelectionProps) => {
    const { selectedDevices } = useDevicesStateContext();
    const [submitting, setSubmitting] = React.useState(false);
    const legacyClient = useWebPortalLegacyClient();
    const webPortalClient = useWebPortalApiClient();

    const queryDeviceSessions = async () => {
        const fetchParentSession = async (device: Device): Promise<BloodGlucoseSession[] | CoughSession[] | Visit[]> => {
            const fetchParentSessionCough = async (serialNumber: string): Promise<CoughSession[]> => {
                const filters = [{
                    Member: 'MediaSerialNumber',
                    Values: [serialNumber],
                },
                {
                    Member: 'StatusId',
                    Values: [CoughSessionStatusEnum.AwaitingData, CoughSessionStatusEnum.ReceivingData, CoughSessionStatusEnum.ReceivingDataMailed],
                }];

                return (await legacyClient.getCoughSessions(filters)).Data;
            }

            const fetchParentSessionCoughVitaloJak2 = async (sessionIds: Array<string>): Promise<CoughSession[]> => {
                const filters = [{
                    Member: 'Id',
                    Values: sessionIds,
                },
                {
                    Member: 'StatusId',
                    Values: [CoughSessionStatusEnum.AwaitingData, CoughSessionStatusEnum.ReceivingData, CoughSessionStatusEnum.ReceivingDataMailed],
                }];

                return (await legacyClient.getCoughSessions(filters)).Data;
            }

            const fetchParentSessionBloodGlucose = async (serialNumber: string): Promise<BloodGlucoseSession[]> => {
                return (await webPortalClient.client.getBloodGlucoseSessions(undefined, `deviceSerialNumber eq '${serialNumber}' and statusId eq 'BGSS_AWAITING_DATA'`)).value;
            }

            const fetchParentVisitDiary = async (serialNumber: string): Promise<Visit[]> => {
                const deployments = await webPortalClient.client.getDiaryPackageDeployments(undefined, `serialNumber eq '${serialNumber}'`);

                if (deployments.value.length === 0)
                    throw new Error(NoSessionAvailable);

                const visitIds = deployments.value.map(d => d.visitId);

                return await (await webPortalClient.client.getVisits(undefined, `id in (${visitIds.join(", ")})`)).value;
            }

            switch (device.type) {
                case DeviceTypes.StatStripXpress2:
                    return fetchParentSessionBloodGlucose(device.serial);
                case DeviceTypes.VitaloJAK1:
                    return fetchParentSessionCough(device.serial);
                case DeviceTypes.VitaloJAK2:
                    const recordings = await VitaloJak2Api.ApiGetRecordings(device.id);
                    if (recordings.length === 0)
                        throw new Error(NoRecordingsPresent);

                    return fetchParentSessionCoughVitaloJak2(recordings.map(r => r.externalCode));
                case DeviceTypes.In2itiveP230EDiary:
                    return fetchParentVisitDiary(device.serial);
            }
        }

        try {
            let entities = [];
            for (let i = 0; i < selectedDevices.length; i++) {

                let parentEntities = [];
                try {
                    parentEntities = await fetchParentSession(selectedDevices[i]);
                }
                catch {
                    return props.dispatch(ReceiveDeviceSessionsAction.create(0, [], []));
                }

                entities = [...entities, ...parentEntities];
            }

            switch (selectedDevices[0].type) {
                case DeviceTypes.StatStripXpress2:
                    const results = await ApiGetResults(selectedDevices[0].id);
                    props.dispatch(ReceiveDeviceSessionsAction.create(results.length, entities, []));
                    break;
                case DeviceTypes.VitaloJAK1:
                    props.dispatch(ReceiveDeviceSessionsAction.create(0, entities, []));
                    break;
                case DeviceTypes.VitaloJAK2:
                    props.dispatch(ReceiveDeviceSessionsAction.create(0, entities, []));
                    break;
                case DeviceTypes.In2itiveP230EDiary:
                    props.dispatch(ReceiveDeviceSessionsAction.create(0, [], entities));
                    break;
                default:
                    props.dispatch(SetUploadStatusAction.create(GenericMessages.DeviceNotSupportedMessage));
                    return props.dispatch(SetInteractionModalStepAction.create(5));

            }
        }
        catch (err) {
            if (err.message) {
                props.dispatch(SetUploadStatusAction.create(err.message));
            } else {
                props.dispatch(SetUploadStatusAction.create(GenericMessages.FailedToUploadMessage));
            }
            props.dispatch(SetInteractionModalStepAction.create(5));
        }
    }

    const handleNavigation = async (step) => {
        await queryDeviceSessions();
        setSubmitting(false);
        props.dispatch(SetInteractionModalStepAction.create(step));       
    }

    const handleChange = (e) => {
        props.dispatch(SelectActionAction.create(e.target.value));
    }

    const handleClose = () => {
        props.dispatch(CloseInteractionModalAction.create());
    }

    const next = async () => {
        const checkDeviceConnection = await ApiDevices();
        if (checkDeviceConnection.length === 0) {
            props.dispatch(SetUploadStatusAction.create(GenericMessages.FailedToConnectMessage));
            props.dispatch(SetInteractionModalStepAction.create(5));
            props.dispatch(SetDeviceOverallUploadStatusAction.create(selectedDevices[0].id, UploadStatus.Failed));
            return;
        }
        
        setSubmitting(true);
        switch (props.selectedOption) {
            case DeviceInteractions.Upload:
                return handleNavigation(2);
            case DeviceInteractions.Mail:
                return handleNavigation(2);
            case DeviceInteractions.ResetDevice:
                return handleNavigation(20);
            case DeviceInteractions.CalibrationVerification:
                return handleNavigation(22);
        }
    }

    if (props.options === undefined) {
        return <Loading />;
    } else {
        const listItems = props.options.map((action) =>
            <li key={action.DeviceInteraction.Id}>
                <input id={action.DeviceInteraction.Id} type="radio" name="action" value={action.DeviceInteraction.Id} checked={props.selectedOption === action.DeviceInteraction.Id} onChange={handleChange} />
                <label htmlFor={action.DeviceInteraction.Id}>
                    <span className="posh-select-icon"><i className={action.DeviceInteraction.Icon} aria-hidden="true"></i></span>
                    <span className="posh-select-body">
                        <span className="posh-select-title">{action.DeviceInteraction.Name}</span>
                        <span className="posh-select-description text-muted">{action.DeviceInteraction.Description}</span>
                    </span>
                </label>
            </li>
        );

        return (
            <div id="ActionSelection">
                <div className="modal-header bg-primary">
                    <h3 className="modal-title">Manage Device</h3>
                </div>
                <div className="modal-body">
                    <h2 className="text-primary">Action</h2>
                    <p style={{ marginBottom: 15 + 'px' }} className="text-muted">Select which action to perform for this device</p>
                    <ul className="posh-select">
                        {listItems.length !== 0 ? listItems : <Alert show={true} category={AlertCategoryEnum.Danger}>There are no actions available or you do not have access to them</Alert>}
                    </ul>
                    <DeviceAlerts />
                </div>
                <div className="modal-footer">
                    <AsyncActionButton
                        disabled={!props.selectedOption || submitting}
                        className="btn btn-primary"
                        isSubmitting={submitting}
                        onClick={next}
                    >
                        Next
                    </AsyncActionButton>
                    <button className="btn btn-primary btn-outline" onClick={handleClose}>Cancel</button>
                </div>
            </div>
        );
    }
}
